import React, { useEffect, useState } from 'react';

const ResetPasswordModal = ({
  user,
  isOpen,
  onClose,
  onSubmit,
  submitting = false,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setConfirmPassword('');
      setError('');
    }
  }, [isOpen, user?.id]);

  if (!isOpen || !user) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (user.role !== 'distributor') {
      setError('Only distributor passwords can be reset here.');
      return;
    }

    if (
      password.length < 9 ||
      password.length > 128 ||
      !/[A-Z]/.test(password) ||
      !/(?:.*[a-z]){3}/.test(password) ||
      !/(?:.*[0-9]){4}/.test(password) ||
      !/[^A-Za-z0-9]/.test(password)
    ) {
      setError(
        'Password must be 9–128 characters and contain an uppercase letter, at least three lowercase letters, four numbers, and a special character.'
      );
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      await onSubmit(user, password);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Unable to reset the password.'
      );
    }
  };

  return (
    <div
      className="admin-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      <section
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-password-title"
      >
        <div className="admin-modal-header">
          <div>
            <h2 id="reset-password-title">Reset Password</h2>
            <p>Set a new password for this distributor account.</p>
          </div>

          <button
            type="button"
            className="admin-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close dialog"
          >
            ×
          </button>
        </div>

        <div className="admin-modal-user">
          <div className="admin-avatar">
            {(user.name || 'U').charAt(0).toUpperCase()}
          </div>

          <div>
            <strong>{user.name}</strong>
            <span>{user.username}</span>
          </div>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          {error && (
            <div className="admin-form-error" role="alert">
              {error}
            </div>
          )}

          <div className="admin-form-field">
            <label htmlFor="reset-new-password">New Password *</label>
            <input
              id="reset-new-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={9}
              maxLength={128}
              autoComplete="new-password"
              required
            />
          </div>

          <div className="admin-form-field">
            <label htmlFor="reset-confirm-password">
              Confirm New Password *
            </label>
            <input
              id="reset-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(event) =>
                setConfirmPassword(event.target.value)
              }
              minLength={9}
              maxLength={128}
              autoComplete="new-password"
              required
            />
          </div>

          <p className="admin-form-help">
            The new password must satisfy your backend's password
            policy. The password is hashed by the backend before storage.
          </p>

          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-button admin-button-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="admin-button admin-button-primary"
              disabled={submitting}
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default ResetPasswordModal;